import { useState } from "react";
import { usePersistentStorage } from "../hooks/useGameHooks";
import { recalculateAllStats } from "../utils/recalculateStats";

/**
 * Player profile page showing overall and per-mode statistics (played, won, win rate,
 * avg guesses, streaks), with a "Recalculate Stats" button to rebuild data from history.
 *
 * @param {{ version: string, currentTheme: string }} props
 * @param {string} props.version - "old" or "new" — controls whether newer modes (trivia, crafts, silhouette) are shown.
 * @param {string} props.currentTheme - Active era theme name.
 * @returns {JSX.Element} The profile/stats page.
 */
function ProfileMode({ version, currentTheme }) {
  const defaultStats = {
    overall: {
      games: 0,
      wins: 0,
      winRate: "0%",
      daysActive: 0,
      streak: 0,
      perfectGames: 0,
    },
    character: {
      played: 0,
      won: 0,
      winRate: "0%",
      avgGuesses: 0,
      currentStreak: 0,
      bestStreak: 0,
      firstTryWins: 0,
      closeCalls: 0,
    },
    quote: {
      played: 0,
      won: 0,
      winRate: "0%",
      avgGuesses: 0,
      currentStreak: 0,
      bestStreak: 0,
      firstTryWins: 0,
      closeCalls: 0,
    },
    music: {
      played: 0,
      won: 0,
      winRate: "0%",
      avgGuesses: 0,
      currentStreak: 0,
      bestStreak: 0,
      firstTryWins: 0,
      closeCalls: 0,
    },
    location: {
      played: 0,
      won: 0,
      winRate: "0%",
      avgGuesses: 0,
      currentStreak: 0,
      bestStreak: 0,
      firstTryWins: 0,
      closeCalls: 0,
    },
    trivia: {
      played: 0,
      won: 0,
      winRate: "0%",
      avgGuesses: 0,
      currentStreak: 0,
      bestStreak: 0,
      firstTryWins: 0,
      closeCalls: 0,
    },
    crafts: {
      played: 0,
      won: 0,
      winRate: "0%",
      avgGuesses: 0,
      currentStreak: 0,
      bestStreak: 0,
      firstTryWins: 0,
      closeCalls: 0,
    },
    silhouette: {
      played: 0,
      won: 0,
      winRate: "0%",
      avgGuesses: 0,
      currentStreak: 0,
      bestStreak: 0,
      firstTryWins: 0,
      closeCalls: 0,
    },
  };

  const [savedStats, setSavedStats] = usePersistentStorage("kisekidle-stats", defaultStats);
  const [isSyncing, setIsSyncing] = useState(false);

  /** Triggers a full scan of localStorage to rebuild stats and collections. */
  const handleRecalculate = () => {
    setIsSyncing(true);
    // Give it a tiny delay for visual feedback
    setTimeout(() => {
      const newStats = recalculateAllStats();
      setSavedStats(newStats);
      setIsSyncing(false);
    }, 800);
  };

  const stats = {
    ...defaultStats,
    ...savedStats,
    trivia: (savedStats && savedStats.trivia) || defaultStats.trivia,
    crafts: (savedStats && savedStats.crafts) || defaultStats.crafts,
    silhouette: (savedStats && savedStats.silhouette) || defaultStats.silhouette,
  };

  /** Reusable stat display — a value with a label underneath. */
  const StatBox = ({ label, value }) => (
    <div className="stat-box">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );

  return (
    <div className={`profile-container theme-${currentTheme}`}>
      <div className="profile-header">
        <div>
          <h2 className="mode-title">📊 Player Profile</h2>
          <p className="mode-subtitle">Your journey through Zemuria</p>
        </div>
        <button
          className={`submit-button recalculate-btn ${isSyncing ? "disabled" : ""}`}
          onClick={handleRecalculate}
          disabled={isSyncing}
        >
          {isSyncing ? "🔄 Syncing..." : "🔄 Recalculate Stats"}
        </button>
      </div>

      <div className="stats-panel overall">
        <div className="stats-panel-header">🌟 Overall Stats</div>
        <div className="stats-grid">
          <StatBox label="Total Games" value={stats.overall.games} />
          <StatBox label="Total Wins" value={stats.overall.wins} />
          <StatBox label="Overall Win Rate" value={stats.overall.winRate} />
          <StatBox label="Days Active" value={stats.overall.daysActive} />
          <StatBox label="All Games Streak" value={stats.overall.streak} />
          <StatBox label="Perfect Games" value={stats.overall.perfectGames} />
        </div>
      </div>

      <div className="stats-panel">
        <div className="stats-panel-header">🎭 Character Mode</div>
        <div className="stats-grid">
          <StatBox label="Played" value={stats.character.played} />
          <StatBox label="Won" value={stats.character.won} />
          <StatBox label="Win Rate" value={stats.character.winRate} />
          <StatBox label="Avg. Guesses" value={stats.character.avgGuesses} />
          <StatBox label="Current Streak" value={stats.character.currentStreak} />
          <StatBox label="Best Streak" value={stats.character.bestStreak} />
        </div>
      </div>

      <div className="stats-panel">
        <div className="stats-panel-header">🎵 Music Mode</div>
        <div className="stats-grid">
          <StatBox label="Played" value={stats.music.played} />
          <StatBox label="Won" value={stats.music.won} />
          <StatBox label="Win Rate" value={stats.music.winRate} />
          <StatBox label="Avg. Guesses" value={stats.music.avgGuesses} />
          <StatBox label="Current Streak" value={stats.music.currentStreak} />
          <StatBox label="Best Streak" value={stats.music.bestStreak} />
        </div>
      </div>

      <div className="stats-panel">
        <div className="stats-panel-header">💬 Quote Mode</div>
        <div className="stats-grid">
          <StatBox label="Played" value={stats.quote.played} />
          <StatBox label="Won" value={stats.quote.won} />
          <StatBox label="Win Rate" value={stats.quote.winRate} />
          <StatBox label="Avg. Guesses" value={stats.quote.avgGuesses} />
          <StatBox label="Current Streak" value={stats.quote.currentStreak} />
          <StatBox label="Best Streak" value={stats.quote.bestStreak} />
        </div>
      </div>

      <div className="stats-panel">
        <div className="stats-panel-header">📍 Location Mode</div>
        <div className="stats-grid">
          <StatBox label="Played" value={stats.location.played} />
          <StatBox label="Won" value={stats.location.won} />
          <StatBox label="Win Rate" value={stats.location.winRate} />
          <StatBox label="Avg. Guesses" value={stats.location.avgGuesses} />
          <StatBox label="Current Streak" value={stats.location.currentStreak} />
          <StatBox label="Best Streak" value={stats.location.bestStreak} />
        </div>
      </div>

      {version === "new" && (
        <>
          <div className="stats-panel">
            <div className="stats-panel-header">🧠 Trivia Mode</div>
            <div className="stats-grid">
              <StatBox label="Played" value={stats.trivia.played} />
              <StatBox label="Won" value={stats.trivia.won} />
              <StatBox label="Win Rate" value={stats.trivia.winRate} />
              <StatBox label="Avg. Guesses" value={stats.trivia.avgGuesses} />
              <StatBox label="Current Streak" value={stats.trivia.currentStreak} />
              <StatBox label="Best Streak" value={stats.trivia.bestStreak} />
            </div>
          </div>
          <div className="stats-panel">
            <div className="stats-panel-header">⚔️ Crafts Mode</div>
            <div className="stats-grid">
              <StatBox label="Played" value={stats.crafts.played} />
              <StatBox label="Won" value={stats.crafts.won} />
              <StatBox label="Win Rate" value={stats.crafts.winRate} />
              <StatBox label="Avg. Guesses" value={stats.crafts.avgGuesses} />
              <StatBox label="Current Streak" value={stats.crafts.currentStreak} />
              <StatBox label="Best Streak" value={stats.crafts.bestStreak} />
            </div>
          </div>
          <div className="stats-panel">
            <div className="stats-panel-header">👥 Silhouette Mode</div>
            <div className="stats-grid">
              <StatBox label="Played" value={stats.silhouette.played} />
              <StatBox label="Won" value={stats.silhouette.won} />
              <StatBox label="Win Rate" value={stats.silhouette.winRate} />
              <StatBox label="Avg. Guesses" value={stats.silhouette.avgGuesses} />
              <StatBox label="Current Streak" value={stats.silhouette.currentStreak} />
              <StatBox label="Best Streak" value={stats.silhouette.bestStreak} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ProfileMode;
